<template>
  <BaseWidget
    class="data-table"
    :title="content.title"
    :widgetId="content.id"
    :edit="edit"
    :persistent="true"
    :content="content"
    :isWidgetLoading="isWidgetLoading"
    :getWidgetReport="getWidgetReport"
  >
    <BaseTable
      :data="data"
      :headers="headers"
      :isWidgetLoading="isWidgetLoading"
      :content="content"
      :summary-row="summaryRow"
      :module="PLAN_MODULE"
      isTableWidget
    />
  </BaseWidget>
</template>

<script lang="ts">
import Vue from "vue";
import { DataOptions } from "vuetify";
import { ThemeOptions } from "vuetify/types/services/theme";
import { mapGetters } from "vuex";

import ProjectWidgetApi from "@/classes/ProjectWidgetApi";
import BaseTable from "@/components/tables/BaseTable.vue";
import BaseWidget from "@/components/widgets/plan/BaseWidget.vue";
import { PLAN_MODULE } from "@/constants";
import csvExport, { buildFileName, buildHeaderRows } from "@/helpers/csvExport";
import { formatters } from "@/helpers/formatters";
import { ContentWidget } from "@/types/layouts";

type TableHeader = {
  text: string;
  value: string;
  format?: string;
};

const HEADER_ORDER: Record<string, number> = {
  plan: 0,
  start_date: 1,
  end_date: 2,
  channel_percentage: 3,
  budget: 4,
};

export default Vue.extend({
  name: "PlanSummaryWidget",

  props: {
    content: Object as () => ContentWidget,
    edit: Boolean,
  },

  components: { BaseWidget, BaseTable },

  data() {
    return {
      PLAN_MODULE,
      options: {} as DataOptions,
      isWidgetLoading: false,
      data: [] as Array<Record<string, string | number | null>>,
      theme: {} as ThemeOptions,
      summaryRow: {} as Record<string, unknown>,
    };
  },

  computed: {
    ...mapGetters("projectOutput", ["projectFiltered"]),

    headers(): TableHeader[] {
      const columns = (this.content?.settings?.columns ?? []) as Array<{ title: string; value: string }>;
      return [...columns]
        .sort(
          (a, b) =>
            (HEADER_ORDER[a.value] ?? Number.MAX_SAFE_INTEGER) - (HEADER_ORDER[b.value] ?? Number.MAX_SAFE_INTEGER)
        )
        .map((column) => ({
          text: column.title,
          value: column.value,
          format:
            column.value === "start_date" || column.value === "end_date"
              ? "formatDate"
              : column.value === "budget"
              ? "formattedCurrencyNoFraction"
              : column.value === "channel_percentage"
              ? "compactPercentFormat"
              : undefined,
        }));
    },
  },

  watch: {
    projectFiltered: {
      immediate: true,
      async handler() {
        this.isWidgetLoading = true;
        await this.getData();
        this.isWidgetLoading = false;
      },
    },
  },

  methods: {
    async getData(): Promise<void> {
      if (!this.projectFiltered) {
        this.data = [];
        return;
      }

      const projectWidgetApi = new ProjectWidgetApi();
      const rows = projectWidgetApi.getPlanSummaryData(this.projectFiltered);
      const totalBudget = rows.reduce((sum, row) => sum + Number(row.budget ?? 0), 0);

      this.data = rows;
      this.summaryRow = {
        budget: totalBudget,
        channel_percentage: rows.length ? 100 : 0,
      };
    },

    formatCellValue(header: TableHeader, value: unknown): string | number {
      if (header.format && typeof formatters[header.format] === "function") {
        return formatters[header.format](value as string | number | null);
      }

      return (value ?? "") as string | number;
    },

    async getWidgetReport(): Promise<void> {
      const title = this.content?.title || "Plan Summary";
      const csvContent: unknown[][] = [];

      csvContent.push(...buildHeaderRows(title));
      csvContent.push([]);
      csvContent.push(this.headers.map((header) => header.text));

      this.data.forEach((row) => {
        csvContent.push(this.headers.map((header) => this.formatCellValue(header, row[header.value])));
      });

      csvContent.push(
        this.headers.map((header, index) => {
          if (index === 0) return "Total";
          return this.formatCellValue(header, this.summaryRow[header.value]);
        })
      );

      csvExport(csvContent, buildFileName(title));
    },
  },
});
</script>
